// LV-10 — what the annual-leave usage rules make of a previewed request.
//
// Shown between "Calculate" and "Submit" so the requester learns of a split-rule problem
// or of where their travel leave lands BEFORE filing, not from a 409. The verdict is the
// server's (`split_check.blocks`); this component only words it. Under `warn` (the
// default) a violation is shown and never blocks, so the form always has a way forward.

import { useTranslation } from "react-i18next";

import { Alert, type AlertSeverity } from "@novemai/components";
import { MAX_ANNUAL_LEAVE_PARTS, MIN_LONGEST_PART_DAYS } from "@novemai/time-attendance-sdk";

import { formatDateRange } from "@/lib/date-range";

import type { LeavePreview, LeaveSplitCheck } from "./leave-api";

type Finding = LeaveSplitCheck["findings"][number];

// Module-level so the keys are plain literals for the extractor and the literal-string lint.
const FINDING_KEY: Readonly<Record<Finding["code"], string>> = {
  part_limit_exceeded: "leave.usage.finding_part_limit_exceeded",
  ten_day_part_unreachable: "leave.usage.finding_ten_day_part_unreachable",
  no_ten_day_part_yet: "leave.usage.finding_no_ten_day_part_yet",
};

function findingSeverity(finding: Finding, blocks: boolean): AlertSeverity {
  if (finding.severity === "advisory") return "info";
  return blocks ? "danger" : "warning";
}

function findingTitleKey(finding: Finding, blocks: boolean): string {
  if (finding.severity === "advisory") return "leave.usage.title_advisory";
  return blocks ? "leave.usage.title_blocked" : "leave.usage.title_warned";
}

/** True when the preview says this request cannot be filed as it stands. */
export function previewBlocksSubmit(preview: LeavePreview | null): boolean {
  return preview?.split_check?.blocks === true;
}

export function LeaveUsageNotes({ preview }: { readonly preview: LeavePreview }): React.ReactElement | null {
  const { t, i18n } = useTranslation();
  const split = preview.split_check ?? null;
  const travel = preview.travel_leave;
  if (split === null && (travel === undefined || travel.days === 0)) return null;

  const days = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });
  return (
    <div className="space-y-3">
      {split !== null && (
        <>
          {split.findings.map((finding) => (
            <Alert
              key={finding.code}
              severity={findingSeverity(finding, split.blocks)}
              title={t(findingTitleKey(finding, split.blocks))}
            >
              {t(FINDING_KEY[finding.code], {
                max: MAX_ANNUAL_LEAVE_PARTS,
                min: MIN_LONGEST_PART_DAYS,
              })}
            </Alert>
          ))}
          <p className="m-0 text-[12.5px] text-[var(--fg-muted)]">
            {t("leave.usage.split_summary", {
              year: split.service_year_index,
              parts: split.part_count,
              longest: days.format(split.longest_part_days),
              remaining: days.format(split.remaining_days),
            })}
          </p>
        </>
      )}
      {travel !== undefined && travel.days > 0 && (
        <p className="m-0 text-[13px] text-[var(--fg-secondary)]">
          {t("leave.usage.travel_summary", {
            days: days.format(travel.days),
            dates: travel.runs
              .map((run) => formatDateRange(i18n.language, run.start_date, run.end_date))
              .join(", "),
          })}
        </p>
      )}
    </div>
  );
}
